<template>
  <div v-if="items" class="ProjectDashboard">
    <header class="ProjectDashboard__masthead">
      <div class="ProjectDashboard__title-block">
        <v-app-bar-nav-icon class="ProjectDashboard__nav-toggle" @click="showDrawer()" />
        <div>
          <span class="zp-overline">{{ $t('projectStream') }}</span>
          <h1 class="ProjectDashboard__title">{{ $t('activity') }}</h1>
          <p class="ProjectDashboard__subtitle">{{ $t('dashboardActivitySubtitle') }}</p>
        </div>
      </div>
    </header>

    <div class="ProjectDashboard__menu-row">
      <DashboardMenu
        class="ProjectDashboard__menu"
        :project-id="projectId"
        :project-type="projectType"
        :can-update-project="can(USER_PERMISSIONS.updateProject)"
      />
    </div>

    <main class="ProjectDashboard__content">
      <section class="ProjectDashboard__panel ProjectDashboard__panel--flush">
        <div class="ProjectDashboard__panel-head">
          <div>
            <span class="zp-overline">{{ $t('dashboardAuditTrail') }}</span>
            <h2>{{ $t('activity') }}</h2>
          </div>
          <div class="ProjectDashboard__filters">
            <v-text-field
              :value="searchText"
              :placeholder="$t('search')"
              hide-details
              dense
              outlined
              clearable
              @input="onSearchInput($event)"
              class="ProjectDashboard__filter ProjectDashboard__filter--wide"
            />
            <v-select
              v-model="objectTypeFilter"
              :items="objectTypeOptions"
              :label="$t('activityObjectType')"
              hide-details
              dense
              outlined
              clearable
              class="ProjectDashboard__filter"
            />
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :search="searchText"
          :custom-filter="filterByDescription"
          class="ProjectDashboard__table"
          :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
          :items-per-page="25"
        >
          <template v-slot:item.created="{ item }">
            <span class="text-no-wrap zp-tabular">{{ item.created | formatDate }}</span>
          </template>

          <template v-slot:item.username="{ item }">
            <span v-if="item.username">{{ item.username }}</span>
            <span v-else class="text--disabled">{{ $t('activitySystemActor') }}</span>
          </template>

          <template v-slot:item.action="{ item }">
            <v-chip
              v-if="actionOf(item)"
              :color="actionColor(actionOf(item))"
              small
              label
              class="font-weight-medium"
            >
              {{ $t(actionLabelKey(actionOf(item))) }}
            </v-chip>
          </template>

          <template v-slot:item.object_type="{ item }">
            <v-chip v-if="item.object_type" small outlined label>
              {{ $t(objectTypeLabelKey(item.object_type)) }}
            </v-chip>
          </template>

          <template v-slot:item.description="{ item }">
            <router-link
              v-if="objectLink(item)"
              :to="objectLink(item)"
              class="text-decoration-none"
            >{{ item.description }}</router-link>
            <span v-else>{{ item.description }}</span>
          </template>
        </v-data-table>

        <div class="ActivityTimeline">
          <article
            v-for="item in activityMobileItems"
            :key="item.id || `${item.created}-${item.description}`"
            class="ActivityTimeline__item"
          >
            <div class="ActivityTimeline__meta">
              <span class="ActivityTimeline__time zp-tabular">{{ item.created | formatDate }}</span>
              <strong>{{ item.username || $t('activitySystemActor') }}</strong>
            </div>
            <div class="ActivityTimeline__tags">
              <span
                v-if="actionOf(item)"
                class="ActivityTimeline__tag"
                :class="`ActivityTimeline__tag--${actionOf(item)}`"
              >
                {{ $t(actionLabelKey(actionOf(item))) }}
              </span>
              <span v-if="item.object_type" class="ActivityTimeline__tag">
                {{ $t(objectTypeLabelKey(item.object_type)) }}
              </span>
            </div>
            <router-link
              v-if="objectLink(item)"
              :to="objectLink(item)"
              class="ActivityTimeline__description"
            >{{ item.description }}</router-link>
            <p v-else class="ActivityTimeline__description">{{ item.description }}</p>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>
<style lang="scss">
.ActivityTimeline {
  display: none;
}

@media (max-width: 700px) {
  .ProjectDashboard__table {
    display: none;
  }

  .ActivityTimeline {
    display: grid;
    gap: var(--zp-space-sm);
    padding: var(--zp-space-sm);

    &__item {
      display: grid;
      gap: var(--zp-space-sm);
      padding: var(--zp-space-sm);
      border: var(--zp-border-hair) solid var(--zp-border-hairline);
      border-radius: var(--zp-radius-md);
      background: var(--zp-bg-page);
    }

    &__meta {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--zp-space-sm);
      color: var(--zp-text-secondary);
      font-size: var(--zp-text-xs);

      strong {
        color: var(--zp-text-primary);
        font-size: var(--zp-text-sm);
        font-weight: 650;
      }
    }

    &__time {
      color: var(--zp-text-tertiary);
    }

    &__tags {
      display: flex;
      flex-wrap: wrap;
      gap: var(--zp-space-2xs);
    }

    &__tag {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      padding: 0 var(--zp-space-xs);
      border: var(--zp-border-hair) solid var(--zp-border-hairline);
      border-radius: var(--zp-radius-sm);
      color: var(--zp-text-secondary);
      font-size: var(--zp-text-xs);
      font-weight: 650;
    }

    &__tag--create {
      background: var(--zp-status-success-bg);
      color: var(--zp-status-success);
    }

    &__tag--update,
    &__tag--run {
      background: var(--zp-status-running-bg);
      color: var(--zp-status-running);
    }

    &__tag--delete {
      background: var(--zp-status-danger-bg);
      color: var(--zp-status-danger);
    }

    &__description {
      margin: 0;
      color: var(--zp-text-primary);
      font-size: var(--zp-text-sm);
      line-height: var(--zp-leading-normal);
      overflow-wrap: anywhere;
      text-decoration: none;
    }

    a.ActivityTimeline__description {
      color: var(--zp-brand);
    }
  }
}
</style>
<script>
import ItemListPageBase from '@/components/ItemListPageBase';
import DashboardMenu from '@/components/DashboardMenu.vue';

// Maps an event description verb to a colored chip + i18n key. Keep verbs
// in sync with helpers.EventLogCreate / EventLogUpdate / EventLogDelete on
// the backend (api/helpers/events.go).
const ACTION_PATTERNS = [
  { re: /\b(created|added)\b/i, key: 'create' },
  { re: /\b(updated|changed|renamed|edited)\b/i, key: 'update' },
  { re: /\b(deleted|removed)\b/i, key: 'delete' },
  { re: /\b(ran|started|executed|launched)\b/i, key: 'run' },
];

const ACTION_COLORS = {
  create: 'success',
  update: 'info',
  delete: 'error',
  run: 'primary',
};

// Object-type → route prefix under /project/:id/. Null means no deep link.
const OBJECT_ROUTES = {
  template: 'templates',
  inventory: 'inventory',
  environment: 'environment',
  key: 'keys',
  repository: 'repositories',
  schedule: 'schedule',
  integration: 'integrations',
  execution_environment: 'execution_environments',
  task: 'history',
};

export default {
  components: { DashboardMenu },

  mixins: [ItemListPageBase],

  data() {
    return {
      objectTypeFilter: null,
    };
  },

  computed: {
    objectTypeOptions() {
      // Drawn from db.EventObjectType — keep visible types in sync.
      return [
        'template', 'inventory', 'environment', 'key', 'repository',
        'schedule', 'integration', 'execution_environment', 'task',
        'project', 'user', 'view',
      ].map((id) => ({
        value: id,
        text: this.$t(this.objectTypeLabelKey(id)),
      }));
    },

    filteredItems() {
      if (!this.objectTypeFilter) return this.items;
      return this.items.filter((e) => e.object_type === this.objectTypeFilter);
    },

    activityMobileItems() {
      return (this.filteredItems || []).slice(0, 25);
    },
  },

  methods: {
    getHeaders() {
      return [
        {
          text: this.$i18n.t('time'), value: 'created', sortable: false, width: 180,
        },
        {
          text: this.$i18n.t('user'), value: 'username', sortable: false, width: 160,
        },
        {
          text: this.$i18n.t('activityAction'), value: 'action', sortable: false, width: 120,
        },
        {
          text: this.$i18n.t('activityObjectType'), value: 'object_type', sortable: false, width: 160,
        },
        { text: this.$i18n.t('description'), value: 'description', sortable: false },
      ];
    },

    getItemsUrl() {
      return `/api/project/${this.projectId}/events/last`;
    },

    actionOf(event) {
      if (!event.description) return null;
      const m = ACTION_PATTERNS.find((p) => p.re.test(event.description));
      return m ? m.key : null;
    },

    actionColor(key) {
      return ACTION_COLORS[key] || 'default';
    },

    actionLabelKey(key) {
      return `activityAction_${key}`;
    },

    objectTypeLabelKey(type) {
      return `activityObjectType_${type}`;
    },

    objectLink(event) {
      if (!event.object_id || !event.object_type || !event.project_id) return null;
      const seg = OBJECT_ROUTES[event.object_type];
      if (!seg) return null;
      // For tasks the dashboard history page does the routing; for everything
      // else we drop the user on the resource list — there's no single
      // canonical "view detail" route for most resources.
      return `/project/${event.project_id}/${seg}`;
    },

    filterByDescription(value, search, item) {
      if (!search) return true;
      const q = search.toLowerCase();
      return (item.description || '').toLowerCase().includes(q)
        || (item.username || '').toLowerCase().includes(q)
        || (item.object_type || '').toLowerCase().includes(q);
    },
  },
};
</script>
